import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  IconHeart,
  IconDroplet,
  IconMan,
  IconRulerMeasure,
  IconPlus,
  IconAlertTriangle,
  IconCheck,
  IconChevronRight,
  IconBulb,
} from "@tabler/icons-react";
import { format } from "date-fns";

import MHeader from "@/components/ui/organisms/MHeader";
import MLoader from "@/components/ui/atoms/MLoader";
import { MButton } from "@/components/ui/atoms/MButtons";
import { getMemberInfo } from "@/hooks/useMembers";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import { useVitalsTracking } from "@/hooks/useVitalsTracking";
import { VitalsParameter, VitalsReading, VitalsRange } from "@/types/vitals";
import { DerivedMemberType } from "@/types/member";

import WhatsAppContact, {
  WhatsAppContactHandle,
} from "@/components/ui/molecules/WhatsAppContact";
import AddReadingModal from "./AddReadingModal";
import AddHeightModal from "./AddHeightModal";
import styles from "./VitalsTracker.module.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

function isSystolicOutOfRange(
  reading: VitalsReading,
  range: VitalsRange
): boolean {
  const { systolic } = reading.values;
  const sr = range.systolic;
  return !!(sr && systolic != null && (systolic < sr[0] || systolic > sr[1]));
}

function isDiastolicOutOfRange(
  reading: VitalsReading,
  range: VitalsRange
): boolean {
  const { diastolic } = reading.values;
  const dr = range.diastolic;
  return !!(
    dr &&
    diastolic != null &&
    (diastolic < dr[0] || diastolic > dr[1])
  );
}

function isOutOfRange(reading: VitalsReading, range: VitalsRange): boolean {
  if (reading.parameter === VitalsParameter.BP) {
    const { systolic, diastolic } = reading.values;
    const sr = range.systolic;
    const dr = range.diastolic;
    if (sr && systolic != null && (systolic < sr[0] || systolic > sr[1]))
      return true;
    if (dr && diastolic != null && (diastolic < dr[0] || diastolic > dr[1]))
      return true;
    return false;
  }
  if (reading.parameter === VitalsParameter.GLUCOSE) {
    const { value, category } = reading.values;
    const nr = getGlucoseRange(category as string | undefined, range);
    if (nr && value != null && (value < nr[0] || value > nr[1])) return true;
    return false;
  }
  return false;
}

const GLUCOSE_CAT_RANGE_KEY: Record<string, keyof VitalsRange> = {
  Fasting: "fasting",
  "Post-prandial": "postPrandial",
  Random: "random",
  "Before bed": "beforeBed",
};

const GLUCOSE_DEFAULT_RANGES: Partial<
  Record<keyof VitalsRange, [number, number]>
> = {
  fasting: [70, 99],
  postPrandial: [70, 139],
  random: [70, 139],
  beforeBed: [80, 140],
};

function getGlucoseRange(
  category: string | undefined,
  range: VitalsRange
): [number, number] | undefined {
  const key = category ? GLUCOSE_CAT_RANGE_KEY[category] : undefined;
  if (!key) return undefined;
  const stored = range[key] as [number, number] | undefined;
  if (Array.isArray(stored) && stored.length >= 2) return stored;
  return GLUCOSE_DEFAULT_RANGES[key];
}

function getOutOfRangeDirection(
  reading: VitalsReading,
  range: VitalsRange
): "high" | "low" | null {
  if (reading.parameter === VitalsParameter.BP) {
    const sys = reading.values.systolic;
    const sr = range.systolic;
    if (sr && sys != null) {
      if (sys > sr[1]) return "high";
      if (sys < sr[0]) return "low";
    }
  }
  if (reading.parameter === VitalsParameter.GLUCOSE) {
    const nr = getGlucoseRange(
      reading.values.category as string | undefined,
      range
    );
    const val = reading.values.value;
    if (nr && val != null) {
      if (val > nr[1]) return "high";
      if (val < nr[0]) return "low";
    }
  }
  return null;
}

function formatReadingNumber(reading: VitalsReading): string {
  const { values, parameter } = reading;
  if (parameter === VitalsParameter.BP) {
    return `${values.systolic ?? "--"}/${values.diastolic ?? "--"}`;
  }
  return String(values.value ?? "--");
}

function formatReadingUnit(reading: VitalsReading): string {
  if (reading.parameter === VitalsParameter.BP) return "mmHg";
  if (reading.parameter === VitalsParameter.GLUCOSE) return "mg/dL";
  if (reading.parameter === VitalsParameter.WEIGHT) return "kg";
  if (reading.parameter === VitalsParameter.HEIGHT) return "cm";
  return "";
}

const BP_CONTEXT_LABEL: Record<string, string> = {
  AFTER_WALK: "After exercise",
  BEFORE_SLEEP: "Before sleep",
  MISSED_MEDICATION: "Missed medicine",
  FEELING_UNWELL: "Feeling unwell",
  AFTER_MEDICATION: "After medicine",
  RESTING: "Resting",
};

function getContextPillLabel(reading: VitalsReading): string | null {
  if (reading.parameter === VitalsParameter.BP && reading.context) {
    return BP_CONTEXT_LABEL[reading.context] ?? reading.context;
  }
  if (
    reading.parameter === VitalsParameter.GLUCOSE &&
    reading.values.category
  ) {
    return reading.values.category as string;
  }
  return null;
}

function formatReadingMeta(reading: VitalsReading): string {
  const dateStr = format(new Date(reading.recordedAt), "d MMM yyyy, h:mm a");
  const parts: string[] = [];

  if (reading.parameter === VitalsParameter.BP) {
    if (reading.values.pulse != null)
      parts.push(`Pulse ${reading.values.pulse}`);
  } else if (reading.parameter === VitalsParameter.WEIGHT) {
    if (reading.values.bmi != null) parts.push(`BMI ${reading.values.bmi}`);
  }

  parts.push(dateStr);
  return parts.join(" • ");
}

function buildOutOfRangeDesc(
  name: string,
  outOfRangeItems: Array<{
    param: VitalsParameter;
    direction: "high" | "low" | null;
  }>
): string {
  const labelMap: Record<VitalsParameter, string> = {
    [VitalsParameter.BP]: "blood pressure",
    [VitalsParameter.GLUCOSE]: "blood sugar reading",
    [VitalsParameter.WEIGHT]: "weight",
    [VitalsParameter.HEIGHT]: "height",
  };

  const parts = outOfRangeItems.map(({ param, direction }) => {
    const label = labelMap[param];
    return direction ? `${label} is ${direction}` : `${label} is out of range`;
  });

  if (parts.length === 0) return "";
  if (parts.length === 1) return `${name}'s latest ${parts[0]}.`;
  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1).join(", ");
  return `${name}'s latest ${rest} and ${last}.`;
}

// ── Status Banner ─────────────────────────────────────────────────────────────

interface StatusBannerProps {
  outOfRangeCount: number;
  hasAnyReading: boolean;
  isOwnProfile: boolean;
  memberFirstName?: string;
  outOfRangeItems: Array<{
    param: VitalsParameter;
    direction: "high" | "low" | null;
  }>;
  onWaBannerClick: () => void;
}

const StatusBanner: React.FC<StatusBannerProps> = ({
  outOfRangeCount,
  hasAnyReading,
  isOwnProfile,
  memberFirstName,
  outOfRangeItems,
  onWaBannerClick,
}) => {
  const { t } = useTranslation();
  const name = memberFirstName ?? "their";

  if (!hasAnyReading) {
    if (!isOwnProfile) return null;
    return (
      <div
        className={`${styles.banner} ${styles.bannerClickable}`}
        onClick={onWaBannerClick}
      >
        <div className={`${styles.bannerIcon} ${styles.bannerIconWhatsApp}`}>
          <IconBulb size={24} color="#36116d" />
        </div>
        <div className={styles.bannerBodyWa}>
          <p className={`${styles.bannerTitle} ${styles.bannerTitleWa}`}>
            {t("vitalsTracker.whatsAppBannerTitle")}
          </p>
          <p className={styles.bannerDescWa}>
            {t("vitalsTracker.whatsAppBannerDesc")}
          </p>
        </div>
        <IconChevronRight size={16} className={styles.bannerChevron} />
      </div>
    );
  }

  if (outOfRangeCount > 0) {
    const titleKey =
      outOfRangeCount === 1
        ? "vitalsTracker.outOfRangeBannerTitle"
        : "vitalsTracker.outOfRangeBannerTitlePlural";
    const desc = buildOutOfRangeDesc(name, outOfRangeItems);
    return (
      <div className={styles.banner}>
        <div className={`${styles.bannerIcon} ${styles.bannerIconAlert}`}>
          <IconAlertTriangle size={20} color="#e08020" />
        </div>
        <div className={styles.bannerBody}>
          <p className={styles.bannerTitle}>
            {t(titleKey, { count: outOfRangeCount })}
          </p>
          {desc && <p className={styles.bannerDesc}>{desc}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.banner}>
      <div className={`${styles.bannerIcon} ${styles.bannerIconSteady}`}>
        <IconCheck size={20} color="#36116d" />
      </div>
      <div className={styles.bannerBody}>
        <p className={`${styles.bannerTitle} ${styles.bannerTitleSteady}`}>
          {t("vitalsTracker.everythingSteady")}
        </p>
        <p className={`${styles.bannerDesc} ${styles.bannerDescSteady}`}>
          {t("vitalsTracker.allVitalsInRange", { name })}
        </p>
      </div>
    </div>
  );
};

// ── Parameter Card ────────────────────────────────────────────────────────────

interface ParameterCardProps {
  parameter: VitalsParameter;
  reading?: VitalsReading;
  range: VitalsRange;
  onAdd: () => void;
  onSeeAll: () => void;
}

const PARAM_CONFIG: Record<
  VitalsParameter,
  {
    labelKey: string;
    iconClass: string;
    Icon: React.FC<{ size: number; color: string }>;
  }
> = {
  [VitalsParameter.BP]: {
    labelKey: "vitalsTracker.bloodPressure",
    iconClass: styles.paramIconBp,
    Icon: ({ size, color }) => <IconHeart size={size} color={color} />,
  },
  [VitalsParameter.GLUCOSE]: {
    labelKey: "vitalsTracker.bloodSugar",
    iconClass: styles.paramIconGlucose,
    Icon: ({ size, color }) => <IconDroplet size={size} color={color} />,
  },
  [VitalsParameter.WEIGHT]: {
    labelKey: "vitalsTracker.weight",
    iconClass: styles.paramIconWeight,
    Icon: ({ size, color }) => <IconMan size={size} color={color} />,
  },
  [VitalsParameter.HEIGHT]: {
    labelKey: "vitalsTracker.heightVital",
    iconClass: styles.paramIconHeight,
    Icon: ({ size, color }) => (
      <span className={styles.rotated90}>
        <IconRulerMeasure size={size} color={color} />
      </span>
    ),
  },
};

const ADULT_PARAMETERS: VitalsParameter[] = [
  VitalsParameter.BP,
  VitalsParameter.GLUCOSE,
  VitalsParameter.WEIGHT,
];
const CHILD_PARAMETERS: VitalsParameter[] = [
  VitalsParameter.WEIGHT,
  VitalsParameter.HEIGHT,
  VitalsParameter.GLUCOSE,
];

const ParameterCard: React.FC<ParameterCardProps> = ({
  parameter,
  reading,
  range,
  onAdd,
  onSeeAll,
}) => {
  const { t } = useTranslation();
  const cfg = PARAM_CONFIG[parameter];
  const outOfRange = reading != null && isOutOfRange(reading, range);
  const pillLabel = reading ? getContextPillLabel(reading) : null;
  const isBP = parameter === VitalsParameter.BP;
  const sysOor =
    isBP && reading != null && isSystolicOutOfRange(reading, range);
  const diaOor =
    isBP && reading != null && isDiastolicOutOfRange(reading, range);

  return (
    <div className={styles.paramCard}>
      <div className={styles.paramRow}>
        <div className={`${styles.paramIcon} ${cfg.iconClass}`}>
          <cfg.Icon size={24} color="#45168c" />
        </div>

        <div className={styles.paramInfo}>
          <p className={styles.paramName}>{t(cfg.labelKey)}</p>

          {reading ? (
            <>
              <div className={styles.readingValueRow}>
                <div className={styles.readingValueLeft}>
                  {isBP ? (
                    <p className={styles.readingValue}>
                      <span
                        className={sysOor ? styles.readingValueOutOfRange : ""}
                      >
                        {reading.values.systolic ?? "--"}
                      </span>
                      {"/"}
                      <span
                        className={diaOor ? styles.readingValueOutOfRange : ""}
                      >
                        {reading.values.diastolic ?? "--"}
                      </span>
                    </p>
                  ) : (
                    <p
                      className={`${styles.readingValue}${
                        outOfRange ? ` ${styles.readingValueOutOfRange}` : ""
                      }`}
                    >
                      {formatReadingNumber(reading)}
                    </p>
                  )}
                  <p className={styles.readingUnit}>
                    {formatReadingUnit(reading)}
                  </p>
                </div>
                {pillLabel && (
                  <span className={styles.contextPill}>{pillLabel}</span>
                )}
              </div>
              <p className={styles.readingMeta}>{formatReadingMeta(reading)}</p>
              <button
                className={styles.seeAllLink}
                onClick={onSeeAll}
                type="button"
              >
                {t("vitalsTracker.seeAllReadings")}
                <IconChevronRight size={13} />
              </button>
            </>
          ) : (
            <button
              className={styles.addFirstReading}
              onClick={onAdd}
              type="button"
            >
              {t("vitalsTracker.addFirstReading")}
            </button>
          )}
        </div>

        <MButton
          variant="link"
          className={styles.addButton}
          onClick={onAdd}
          type="button"
          aria-label={t("vitalsTracker.addReading")}
        >
          <IconPlus size={20} color="#36116d" />
        </MButton>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const VitalsTracker: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const { navigateBackIfExists } = useNavigationSource();
  const {
    config,
    isLoading: configLoading,
    refreshConfig,
  } = useVitalsTracking(memberInfo?.id ?? "");

  const [addModalParam, setAddModalParam] = useState<VitalsParameter | null>(
    null
  );
  const [addHeightReadingOpen, setAddHeightReadingOpen] = useState(false);
  const whatsAppRef = useRef<WhatsAppContactHandle>(null);

  const isPet = memberInfo?.derivedMemberType === DerivedMemberType.P;
  const isChild = memberInfo?.derivedMemberType === DerivedMemberType.C;
  const activeParameters = isChild ? CHILD_PARAMETERS : ADULT_PARAMETERS;

  const handleBack = useCallback(() => {
    if (!navigateBackIfExists()) navigate("/dashboard");
  }, [navigate, navigateBackIfExists]);

  const latestReadings = config?.latestReadings ?? {};

  const hasAnyReading = activeParameters.some((p) => latestReadings[p] != null);

  const outOfRangeItems = activeParameters
    .filter((param) => {
      const reading = latestReadings[param];
      return reading != null && config?.ranges?.[param] != null
        ? isOutOfRange(reading, config.ranges[param]!)
        : false;
    })
    .map((param) => ({
      param,
      direction: getOutOfRangeDirection(
        latestReadings[param]!,
        config?.ranges?.[param] ?? {}
      ),
    }));

  const handleAddSuccess = useCallback(async () => {
    await refreshConfig();
  }, [refreshConfig]);

  const handleSeeAll = useCallback(
    (param: VitalsParameter) => {
      navigate(`/vitals-tracker/readings/${param}`);
    },
    [navigate]
  );

  return (
    <div className={styles.page}>
      <div className={styles.headerSection}>
        <MHeader
          module="vitalsTracker"
          subHeading={t("vitalsTracker.title")}
          memberInfo={memberInfo}
          showProfilePickerRow
          hidePetsInPicker
          disableSticky
          useParentGradient
          hideLearnMore
          onBack={handleBack}
          className={styles.flushHeader}
        />
        {!configLoading && (
          <div className={styles.bannerWrap}>
            <StatusBanner
              outOfRangeCount={outOfRangeItems.length}
              hasAnyReading={hasAnyReading}
              isOwnProfile={!memberInfo?.onBehalf}
              memberFirstName={memberInfo?.nickname ?? memberInfo?.firstName}
              outOfRangeItems={outOfRangeItems}
              onWaBannerClick={() => whatsAppRef.current?.send()}
            />
          </div>
        )}
      </div>

      {configLoading ? (
        <div className={styles.loaderWrap}>
          <MLoader />
        </div>
      ) : isPet ? (
        <div className={styles.content}>
          <p className={styles.emptyText}>
            {t("vitalsTracker.noVitalsForPet")}
          </p>
        </div>
      ) : (
        <div className={styles.content}>
          {activeParameters.map((param) => (
            <ParameterCard
              key={param}
              parameter={param}
              reading={latestReadings[param]}
              range={config?.ranges?.[param] ?? {}}
              onAdd={() => {
                if (param === VitalsParameter.HEIGHT)
                  setAddHeightReadingOpen(true);
                else setAddModalParam(param);
              }}
              onSeeAll={() => handleSeeAll(param)}
            />
          ))}
        </div>
      )}

      {addModalParam && memberInfo?.id && (
        <AddReadingModal
          isOpen={addModalParam !== null}
          onClose={() => setAddModalParam(null)}
          parameter={addModalParam}
          memberId={memberInfo.id}
          onSuccess={handleAddSuccess}
          isFirstTime={!config?.enabledParameters?.includes(addModalParam)}
        />
      )}
      {memberInfo?.id && (
        <AddHeightModal
          isOpen={addHeightReadingOpen}
          onClose={() => setAddHeightReadingOpen(false)}
          memberId={memberInfo.id}
          mode="reading"
          onSuccess={handleAddSuccess}
        />
      )}

      <WhatsAppContact ref={whatsAppRef} />
    </div>
  );
};

export default VitalsTracker;
